import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import DashboardLayout from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Plus, Trash, PencilSimple, AddressBook, X, Warning } from '@phosphor-icons/react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const emptyForm = { name: '', emails: ['', ''] };

const VendorDirectory = () => {
  const { getAuthHeader } = useAuth();
  const [vendors, setVendors] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/vendors`, { headers: getAuthHeader() });
      setVendors(res.data);
    } catch (e) {
      toast.error('Failed to load vendors');
    }
  };

  useEffect(() => { load(); }, []);

  const openAdd = () => { setEditing(null); setForm(emptyForm); setDialogOpen(true); };
  const openEdit = (v) => { setEditing(v); setForm({ name: v.name, emails: [...v.emails] }); setDialogOpen(true); };

  const setEmail = (idx, val) => {
    const emails = [...form.emails];
    emails[idx] = val;
    setForm({ ...form, emails });
  };
  const addEmailField = () => setForm({ ...form, emails: [...form.emails, ''] });
  const removeEmailField = (idx) => setForm({ ...form, emails: form.emails.filter((_, i) => i !== idx) });

  const filledEmails = form.emails.map((e) => e.trim()).filter(Boolean);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Vendor name is required'); return; }
    if (filledEmails.length === 0) { toast.error('At least one email is required'); return; }
    setSaving(true);
    try {
      const payload = { name: form.name.trim(), emails: filledEmails };
      if (editing) {
        await axios.put(`${API}/vendors/${editing.id}`, payload, { headers: getAuthHeader() });
        toast.success('Vendor updated');
      } else {
        await axios.post(`${API}/vendors`, payload, { headers: getAuthHeader() });
        toast.success('Vendor added to directory');
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save vendor');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (v) => {
    if (!window.confirm(`Delete vendor "${v.name}" from the directory?`)) return;
    try {
      await axios.delete(`${API}/vendors/${v.id}`, { headers: getAuthHeader() });
      toast.success('Vendor deleted');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete vendor');
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-4xl md:text-5xl font-extrabold tracking-tighter text-text-primary">
              Vendor Directory
            </h1>
            <p className="mt-2 text-text-secondary">Vendor contacts used for RFQ emails and PO dispatch</p>
          </div>
          <Button className="bg-accent-brand hover:bg-accent-brand-hover text-white" onClick={openAdd} data-testid="add-vendor-button">
            <Plus size={18} className="mr-2" weight="bold" />
            Add Vendor
          </Button>
        </div>

        <div className="bg-white border border-border-default rounded">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-surface border-b border-border-default">
                <tr>
                  <th className="text-left p-4 text-xs font-bold uppercase tracking-[0.2em] text-text-secondary">Vendor</th>
                  <th className="text-left p-4 text-xs font-bold uppercase tracking-[0.2em] text-text-secondary">Email Contacts</th>
                  <th className="text-left p-4 text-xs font-bold uppercase tracking-[0.2em] text-text-secondary">Added</th>
                  <th className="text-right p-4 text-xs font-bold uppercase tracking-[0.2em] text-text-secondary">Actions</th>
                </tr>
              </thead>
              <tbody>
                {vendors.length === 0 && (
                  <tr><td colSpan="4" className="p-8 text-center text-text-tertiary">No vendors yet. Add your first vendor to enable RFQ emails and PO dispatch.</td></tr>
                )}
                {vendors.map((v) => (
                  <tr key={v.id} className="border-b border-border-default hover:bg-surface transition-colors" data-testid={`vendor-row-${v.id}`}>
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-accent-brand-subtle flex items-center justify-center">
                          <AddressBook size={20} className="text-accent-brand" weight="duotone" />
                        </div>
                        <span className="font-medium text-text-primary">{v.name}</span>
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="flex flex-wrap gap-1.5 items-center">
                        {v.emails.map((em) => (
                          <span key={em} className="px-2 py-0.5 rounded-full text-xs bg-surface border border-border-default text-text-secondary font-mono">{em}</span>
                        ))}
                        {v.emails.length === 1 && (
                          <span className="inline-flex items-center gap-1 text-xs text-amber-700" title="Only one contact on file — consider adding a second">
                            <Warning size={14} weight="fill" /> 1 contact only
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="p-4 text-text-secondary text-sm">{new Date(v.created_at).toLocaleDateString()}</td>
                    <td className="p-4 text-right">
                      <div className="flex gap-2 justify-end">
                        <Button size="sm" variant="outline" onClick={() => openEdit(v)} data-testid={`edit-vendor-${v.id}`}>
                          <PencilSimple size={16} />
                        </Button>
                        <Button size="sm" variant="outline" className="text-status-destructive" onClick={() => handleDelete(v)} data-testid={`delete-vendor-${v.id}`}>
                          <Trash size={16} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{editing ? 'Edit Vendor' : 'Add Vendor'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSave} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="vendor-name">Vendor Name</Label>
                <Input
                  id="vendor-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g., Sigma-Aldrich"
                  required
                  data-testid="vendor-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Email Contacts</Label>
                {form.emails.map((em, idx) => (
                  <div key={idx} className="flex gap-2">
                    <Input
                      type="email"
                      value={em}
                      onChange={(e) => setEmail(idx, e.target.value)}
                      placeholder={`contact${idx + 1}@vendor.com`}
                      data-testid={`vendor-email-input-${idx}`}
                    />
                    {form.emails.length > 1 && (
                      <Button type="button" size="icon" variant="outline" onClick={() => removeEmailField(idx)} data-testid={`remove-email-${idx}`}>
                        <X size={16} />
                      </Button>
                    )}
                  </div>
                ))}
                <Button type="button" size="sm" variant="outline" onClick={addEmailField} data-testid="add-email-field-button">
                  <Plus size={14} className="mr-1" /> Add another email
                </Button>
                {filledEmails.length === 1 && (
                  <p className="flex items-center gap-1.5 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2" data-testid="single-email-warning">
                    <Warning size={14} weight="fill" />
                    Only 1 email provided — we recommend at least 2 contacts per vendor. You can still save.
                  </p>
                )}
              </div>
              <Button type="submit" disabled={saving} className="w-full bg-accent-brand hover:bg-accent-brand-hover text-white" data-testid="save-vendor-button">
                {saving ? 'Saving…' : editing ? 'Update Vendor' : 'Add Vendor'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default VendorDirectory;
